import Vue from 'vue'
import auth0 from 'auth0-js'
import mfaApi from '@/apis/mfa'
import { parseOtpAuthenticators, parseAssociateResponse } from '@/utils/mfaHelpers'
import {
  MFA_SET_TOKEN,
  MFA_CLEAR,
  MFA_SET_AUTHENTICATORS,
  MFA_SET_PENDING_ENROLLMENT,
  MFA_CLEAR_PENDING_ENROLLMENT,
  MFA_SET_ENROLL_STATUS
} from '../mutation-types'

// Dedicated WebAuth instance for the MFA API.
// - domain: the custom domain (where the user's SSO session cookie lives)
// - audience: the canonical tenant /mfa/ API (custom domain is not a valid audience)
// - responseType 'token': we only need the MFA-scoped access token, no id_token
// Obtaining this token requires an interactive authorize() redirect because Auth0
// enforces an MFA step-up for the MFA API, which checkSession (prompt=none) cannot do.
function getMfaWebAuth () {
  return new auth0.WebAuth({
    domain: Vue.env.authDomain,
    clientID: Vue.env.authClientID,
    redirectUri: window.location.origin + '/account/security',
    responseType: 'token',
    audience: Vue.env.mfaAudience,
    scope: 'enroll read:authenticators remove:authenticators'
  })
}

const state = {
  mfaAccessToken: null,
  authenticators: [],
  pendingEnrollment: null, // { secret, barcodeUri, recoveryCodes }
  status: {
    enroll: null // null | 'requested' | 'successful' | 'failed'
  }
}

const getters = {
  mfaAuthenticators: function (state) { return state.authenticators },
  mfaPendingEnrollment: function (state) { return state.pendingEnrollment },
  hasMfaToken: function (state) { return !!state.mfaAccessToken },
  statusMfaEnroll: function (state) { return state.status.enroll }
}

const actions = {
  // Try to get the MFA token silently (no redirect) from the existing session.
  // Succeeds only when Auth0 does not need an interactive MFA step-up and the
  // SSO cookie is reachable (i.e. not blocked as a third-party cookie). Callers
  // fall back to startMfaAuthorize() when this rejects.
  initMfaSession: function ({ commit }) {
    return new Promise((resolve, reject) => {
      getMfaWebAuth().checkSession({}, function (err, authResult) {
        if (err) {
          reject(err)
        } else if (authResult && authResult.accessToken) {
          commit(MFA_SET_TOKEN, authResult.accessToken)
          resolve(authResult.accessToken)
        } else {
          reject(new Error('no_token'))
        }
      })
    })
  },
  // Full-page redirect to Auth0 to authenticate (with MFA step-up) and return an
  // MFA-scoped access token in the URL hash at /account/security.
  startMfaAuthorize: function () {
    getMfaWebAuth().authorize()
  },
  // Parse the MFA-scoped access token from the hash after returning from authorize().
  resumeMfaSession: function ({ commit }) {
    return new Promise((resolve, reject) => {
      getMfaWebAuth().parseHash({ hash: window.location.hash }, function (err, authResult) {
        if (err) {
          reject(err)
        } else if (authResult && authResult.accessToken) {
          commit(MFA_SET_TOKEN, authResult.accessToken)
          resolve(authResult.accessToken)
        } else {
          reject(new Error('no_token'))
        }
      })
    })
  },
  loadMfaAuthenticators: function ({ commit, state }) {
    return mfaApi.getAuthenticators(state.mfaAccessToken).then(function (res) {
      commit(MFA_SET_AUTHENTICATORS, parseOtpAuthenticators(res.body))
    })
  },
  startMfaEnrollment: function ({ commit, state }) {
    return mfaApi.associateOtp(state.mfaAccessToken).then(function (res) {
      commit(MFA_SET_PENDING_ENROLLMENT, parseAssociateResponse(res.body))
    })
  },
  confirmMfaEnrollment: function ({ commit, state, dispatch }, { otp }) {
    commit(MFA_SET_ENROLL_STATUS, 'requested')
    return mfaApi.confirmOtp(state.mfaAccessToken, otp).then(function () {
      commit(MFA_CLEAR_PENDING_ENROLLMENT)
      return dispatch('loadMfaAuthenticators').then(function () {
        commit(MFA_SET_ENROLL_STATUS, 'successful')
      })
    }).catch(function (err) {
      commit(MFA_SET_ENROLL_STATUS, 'failed')
      throw err
    })
  },
  removeMfaAuthenticator: function ({ state, dispatch }, { id }) {
    return mfaApi.deleteAuthenticator(state.mfaAccessToken, id).then(function () {
      return dispatch('loadMfaAuthenticators')
    })
  },
  clearMfaSession: function ({ commit }) {
    commit(MFA_CLEAR)
  }
}

const mutations = {
  [MFA_SET_TOKEN]: function (state, token) { state.mfaAccessToken = token },
  [MFA_CLEAR]: function (state) {
    state.mfaAccessToken = null
    state.authenticators = []
    state.pendingEnrollment = null
  },
  [MFA_SET_AUTHENTICATORS]: function (state, list) { state.authenticators = list },
  [MFA_SET_PENDING_ENROLLMENT]: function (state, enrollment) { state.pendingEnrollment = enrollment },
  [MFA_CLEAR_PENDING_ENROLLMENT]: function (state) { state.pendingEnrollment = null },
  [MFA_SET_ENROLL_STATUS]: function (state, status) {
    state.status = { ...state.status, enroll: status }
  }
}

export default { state, getters, actions, mutations }



// WEBPACK FOOTER //
// ./src/store/modules/mfa.js